import React, { useRef, useState, useEffect } from "react";
// import EmojiPicker from 'emoji-picker-react';
import { EmojiPicker,  } from '@ferrucc-io/emoji-picker';

// Simple emoji list for demonstration
const EMOJIS = ["😀", "😂", "😍", "😎", "👍", "🎉", "🔥", "🥳", "🤔", "🙌", "💡", "🚀"];

const PLACEHOLDER = "What is on your mind ?";

// Helper to get caret character offset within a node
function getCaretCharacterOffsetWithin(element) {
  let caretOffset = 0, sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    let range = sel.getRangeAt(0);
    let preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    caretOffset = preCaretRange.toString().length;
    return {
      start: caretOffset - range.toString().length,
      end: caretOffset
    };
  }
  return { start: 0, end: 0 };
}

// Helper to set caret by character offset
function setCaretPosition(element, start, end = start) {
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let charCount = 0;
  let startNode = null;
  let endNode = null;
  let startOffset = 0;
  let endOffset = 0;

  let node;
  while (node = walker.nextNode()) {
    const nodeLength = node.textContent.length;

    if (startNode === null && charCount + nodeLength >= start) {
      startNode = node;
      startOffset = start - charCount;
    }

    if (endNode === null && charCount + nodeLength >= end) {
      endNode = node;
      endOffset = end - charCount;
      break;
    }

    charCount += nodeLength;
  }

  if (startNode) {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode || startNode, endOffset || startOffset);

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

// Utility: Get word count from HTML content
export function getWordCountFromHtml(html) {
  if (!html) return 0;
  // Remove HTML tags and decode entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || '';
  // Split by whitespace and filter out empty strings
  return text.trim().split(/\s+/).filter(Boolean).length;
}

const RichTextEditor = ({ value = '', onChange }) => {
  const editorRef = useRef(null);
  const [showEmojis, setShowEmojis] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const selectionOffsets = useRef({ start: 0, end: 0 });
  const skipNextSync = useRef(false);
  const isProgrammaticChange = useRef(false);

  // Set editor content when value changes (controlled) - but skip if we just made a change
  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }

    if (editorRef.current && editorRef.current.innerHTML !== value) {
      const currentOffsets = getCaretCharacterOffsetWithin(editorRef.current);
      editorRef.current.innerHTML = value || '';

      // Restore cursor position after sync
      if (document.activeElement === editorRef.current) {
        setTimeout(() => {
          setCaretPosition(editorRef.current, currentOffsets.start, currentOffsets.end);
        }, 0);
      }
    }
  }, [value]);

  // Save selection as offsets
  const saveSelection = () => {
    if (editorRef.current && document.activeElement === editorRef.current) {
      selectionOffsets.current = getCaretCharacterOffsetWithin(editorRef.current);
    }
  };

  // Format command (bold, italic)
  const handleFormat = (command) => {
    document.execCommand(command, false, null);
    focusEditor();
    handleInput();
  };

  // Linkify plain text URLs that are not already inside anchors
  const linkifyEditorContent = () => {
    if (!editorRef.current) return false;
    const root = editorRef.current;
    let didChange = false;

    const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    const nodesToProcess = [];
    let node;
    while ((node = walker.nextNode())) {
      const parentEl = node.parentElement;
      if (!parentEl) continue;
      if (parentEl.closest('a')) continue; // skip text inside links
      const text = node.nodeValue || '';
      if (urlRegex.test(text)) {
        nodesToProcess.push(node);
      }
      urlRegex.lastIndex = 0; // reset regex state
    }

    nodesToProcess.forEach((textNode) => {
      const text = textNode.nodeValue || '';
      const frag = document.createDocumentFragment();
      let lastIndex = 0;
      text.replace(urlRegex, (match, p1, p2, offset) => {
        if (offset > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
        }
        let href = match;
        if (/^www\./i.test(href)) {
          href = 'https://' + href;
        }
        const a = document.createElement('a');
        a.href = href;
        a.textContent = match;
        frag.appendChild(a);
        lastIndex = offset + match.length;
        return match;
      });
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      if (frag.childNodes.length > 0) {
        textNode.parentNode.replaceChild(frag, textNode);
        didChange = true;
      }
    });

    return didChange;
  };

  // Handle input and propagate changes
  const handleInput = () => {
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }
    if (!editorRef.current) return;

    // Save caret, linkify, then notify parent
    const currentOffsets = getCaretCharacterOffsetWithin(editorRef.current);
    const changed = linkifyEditorContent();
    if (changed) {
      // prevent recursive onInput triggers
      isProgrammaticChange.current = true;
      // restore caret best-effort
      setCaretPosition(editorRef.current, currentOffsets.start, currentOffsets.end);
    }
    if (onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Insert emoji at caret position in HTML string and update via onChange
  const insertEmoji = (emoji) => {
    if (!editorRef.current) return;
    // Get current HTML and caret offsets
    const html = editorRef.current.innerHTML;
    const { start, end } = selectionOffsets.current;

    // Convert HTML to plain text to get mapping
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';

    // Insert emoji in plain text, then map back to HTML
    // Instead, we need to insert emoji at the correct offset in the HTML string
    // We'll use a range to do this in a temporary element
    let newHtml = html;
    if (start === end) {
      // Insert at caret
      // Use a range to insert emoji at the correct offset in the HTML
      const range = document.createRange();
      let charIndex = 0;
      let found = false;
      function traverse(node) {
        if (found) return;
        if (node.nodeType === 3) {
          const nextCharIndex = charIndex + node.length;
          if (start >= charIndex && start <= nextCharIndex) {
            range.setStart(node, start - charIndex);
            range.collapse(true);
            found = true;
          }
          charIndex = nextCharIndex;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            traverse(node.childNodes[i]);
            if (found) break;
          }
        }
      }
      traverse(tempDiv);
      if (found) {
        range.insertNode(document.createTextNode(emoji));
        newHtml = tempDiv.innerHTML;
      } else {
        // fallback: append
        newHtml = html + emoji;
      }
    } else {
      // Replace selection
      // Similar logic, but delete contents between start and end
      const range = document.createRange();
      let charIndex = 0;
      let foundStart = false, foundEnd = false;
      function traverse(node) {
        if (foundEnd) return;
        if (node.nodeType === 3) {
          const nextCharIndex = charIndex + node.length;
          if (!foundStart && start >= charIndex && start <= nextCharIndex) {
            range.setStart(node, start - charIndex);
            foundStart = true;
          }
          if (foundStart && end >= charIndex && end <= nextCharIndex) {
            range.setEnd(node, end - charIndex);
            foundEnd = true;
          }
          charIndex = nextCharIndex;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            traverse(node.childNodes[i]);
            if (foundEnd) break;
          }
        }
      }
      traverse(tempDiv);
      if (foundStart) {
        range.deleteContents();
        range.insertNode(document.createTextNode(emoji));
        newHtml = tempDiv.innerHTML;
      } else {
        // fallback: append
        newHtml = html + emoji;
      }
    }
    // Update parent state
    if (onChange) onChange(newHtml);
    setShowEmojis(false);
  };

  // Focus the editor div
  const focusEditor = () => {
    editorRef.current && editorRef.current.focus();
  };

  // Show placeholder if empty
  const showPlaceholder = (!value && !isFocused) || !value || (value && value.length === 0);

  // Handle emoji selection from EmojiPicker
  const handleEmojiClick = (emojiData) => {
    insertEmoji(emojiData);
  };

  // When emoji picker is opened, save current selection
  const handleEmojiButtonClick = () => {
    saveSelection();
    setShowEmojis((v) => !v);
  };

  return (
    <div className="w-full bg-white px-2">
      <div className="flex mb-1 px-2">
        <button
          type="button"
          className="rounded bg-white hover:bg-[#cce2fa] hover:color-[#222f3e] w-[34px] h-[28px] text-center flex items-center justify-center"
          title="Bold"
          onClick={() => handleFormat("bold")}
        >
          <svg width="24" height="24" focusable="false"><path d="M7.8 19c-.3 0-.5 0-.6-.2l-.2-.5V5.7c0-.2 0-.4.2-.5l.6-.2h5c1.5 0 2.7.3 3.5 1 .7.6 1.1 1.4 1.1 2.5a3 3 0 0 1-.6 1.9c-.4.6-1 1-1.6 1.2.4.1.9.3 1.3.6s.8.7 1 1.2c.4.4.5 1 .5 1.6 0 1.3-.4 2.3-1.3 3-.8.7-2.1 1-3.8 1H7.8Zm5-8.3c.6 0 1.2-.1 1.6-.5.4-.3.6-.7.6-1.3 0-1.1-.8-1.7-2.3-1.7H9.3v3.5h3.4Zm.5 6c.7 0 1.3-.1 1.7-.4.4-.4.6-.9.6-1.5s-.2-1-.7-1.4c-.4-.3-1-.4-2-.4H9.4v3.8h4Z" fillRule="evenodd"></path></svg>
        </button>
        <button
          type="button"
          className="rounded bg-white hover:bg-[#cce2fa] hover:color-[#222f3e] w-[34px] h-[28px] text-center flex items-center justify-center"
          title="Italic"
          onClick={() => handleFormat("italic")}
        >
          <svg width="24" height="24" focusable="false"><path d="m16.7 4.7-.1.9h-.3c-.6 0-1 0-1.4.3-.3.3-.4.6-.5 1.1l-2.1 9.8v.6c0 .5.4.8 1.4.8h.2l-.2.8H8l.2-.8h.2c1.1 0 1.8-.5 2-1.5l2-9.8.1-.5c0-.6-.4-.8-1.4-.8h-.3l.2-.9h5.8Z" fillRule="evenodd"></path></svg>
        </button>
        <div className="relative">
          <button
            type="button"
            className="rounded bg-white hover:bg-[#cce2fa] hover:color-[#222f3e] w-[34px] h-[28px] text-center flex items-center justify-center"
            title="Insert Emoji"
            onClick={handleEmojiButtonClick}
          >
            <svg width="24" height="24" focusable="false"><path d="M9 11c.6 0 1-.4 1-1s-.4-1-1-1a1 1 0 0 0-1 1c0 .6.4 1 1 1Zm6 0c.6 0 1-.4 1-1s-.4-1-1-1a1 1 0 0 0-1 1c0 .6.4 1 1 1Zm-3 5.5c2.1 0 4-1.5 4.4-3.5H7.6c.5 2 2.3 3.5 4.4 3.5ZM12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm0 14.5a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13Z" fillRule="nonzero"></path></svg>
          </button>
          {showEmojis && (
            <div className="absolute z-10 mt-2 p-2 bg-white border border-gray-300 rounded shadow-lg">
              {/* <EmojiPicker onEmojiClick={handleEmojiClick} height={350} width={300} /> */}

              <EmojiPicker onEmojiSelect={handleEmojiClick}>
                <EmojiPicker.Header>
                  <EmojiPicker.Input placeholder="Search emoji" />
                </EmojiPicker.Header>
                <EmojiPicker.Group>
                  <EmojiPicker.List />
                </EmojiPicker.Group>
              </EmojiPicker>
            </div>
          )}
        </div>
      </div>
      <div
        ref={editorRef}
        className={`min-h-[180px] border-none border-gray-300 rounded-md p-4 bg-white  transition-all text-[16px]   focus:ring-2 focus:ring-white outline-none wefayo-editor`}
        contentEditable
        suppressContentEditableWarning
        spellCheck={true}
        tabIndex={0}
        aria-label="Rich text editor"
        style={{
          fontFamily: 'Helvetica,Arial,sans-serif',
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          position: 'relative',
        }}
        onInput={handleInput}
        onFocus={e => { setIsFocused(true); saveSelection(); }}
        onBlur={() => setIsFocused(false)}
        onClick={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        data-placeholder={PLACEHOLDER}
      />
      {showPlaceholder && (
        <div
          className="pointer-events-none select-none text-[#6b7280] text-[16px] absolute mt-[-160px] ml-5"
          style={{ zIndex: 1 }}
        >
          {PLACEHOLDER}
        </div>
      )}

      {/* Debug info */}
      {/* <div className="mt-4 p-2 bg-gray-100 rounded text-sm">
        <div><strong>Current selection:</strong> {selectionOffsets.current.start} - {selectionOffsets.current.end}</div>
        <div><strong>Content length:</strong> {editorRef.current?.textContent?.length || 0}</div>
        <div><strong>HTML content:</strong> {value?.substring(0, 100)}...</div>
      </div> */}
    </div>
  );
};

export default RichTextEditor;